import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getProducts, getCategories, getCompanies, searchStores } from '@/lib/api';
import { getFirstImage } from '@/utils/imageUtils';
import { useLocale } from '@/contexts/LocaleContext';
import useTranslation from '@/hooks/useTranslation';
import MobileBottomNav from '@/components/layout/MobileBottomNav';
import PublicHeader from '@/components/layout/PublicHeader';
import { 
  Search as SearchIcon, 
  SlidersHorizontal, 
  MapPin, 
  Package,
  Building2,
  Filter,
  X,
  ImageIcon,
  CheckCircle2,
  Wrench,
  ArrowLeft,
  Factory,
  Users,
  Store,
  ExternalLink,
  Star,
  ShoppingBag,
  ZoomIn
} from 'lucide-react';

// Image Modal Component
const ImageModal = ({ isOpen, onClose, imageSrc, title }) => {
  if (!isOpen) return null;
  
  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
    >
      <div className="relative max-w-4xl max-h-[90vh] w-full">
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
        >
          <X className="w-8 h-8" />
        </button>
        {title && (
          <p className="text-white text-center mb-2 text-lg font-medium">{title}</p>
        )}
        <img 
          src={imageSrc} 
          alt={title || 'Enlarged view'}
          className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>,
    document.body
  );
};

const MEXICAN_STATES = [
  'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche', 'Chiapas',
  'Chihuahua', 'Ciudad de México', 'Coahuila', 'Colima', 'Durango', 'Estado de México',
  'Guanajuato', 'Guerrero', 'Hidalgo', 'Jalisco', 'Michoacán', 'Morelos', 'Nayarit',
  'Nuevo León', 'Oaxaca', 'Puebla', 'Querétaro', 'Quintana Roo', 'San Luis Potosí',
  'Sinaloa', 'Sonora', 'Tabasco', 'Tamaulipas', 'Tlaxcala', 'Veracruz', 'Yucatán', 'Zacatecas'
];

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

const Search = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isUSA, formatPrice, region } = useLocale();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  
  // Get states list based on region
  const statesList = isUSA ? US_STATES : MEXICAN_STATES;
  
  // Get initial search type from URL param or default to 'catalogo' (featured products)
  const initialType = searchParams.get('type') || 'catalogo';
  const [searchType, setSearchType] = useState(initialType); // catalogo (featured), tiendas, providers, services, fabricantes
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]); // Online stores (companies with stores)
  const [companies, setCompanies] = useState([]);
  const [categories, setCategories] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [regionFilter, setRegionFilter] = useState(region); // Use user's region as default
  const [imageModal, setImageModal] = useState({ isOpen: false, src: '', title: '' });
  const [filters, setFilters] = useState({
    search: searchParams.get('q') || '',
    category: searchParams.get('category') || '',
    subcategory: '',
    origin: '',
    availability: '',
    sample_available: '',
    state: '',
    company_type: '',
    verified: '',
    // Filtros de subcategorías
    proveedor_subcategoria: '',
    maquilero_subcategoria: '',
    // Tipo de producto para catálogo
    product_type: '' // telas, avios, ropa
  });

  // Product types for catalog filter
  const productTypes = [
    { value: '', label: isUSA ? 'All types' : 'Todos los tipos' },
    { value: 'telas', label: isUSA ? 'Fabrics' : 'Telas' },
    { value: 'avios', label: isUSA ? 'Trims & Accessories' : 'Avíos y Habilitación' },
    { value: 'ropa', label: isUSA ? 'Clothing' : 'Ropa' }
  ];

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const cats = await getCategories(isUSA ? 'en' : 'es');
        setCategories(cats);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (searchType === 'catalogo') {
          // Catálogo:
          // - Sin búsqueda: mostrar SOLO productos destacados
          // - Con búsqueda: mostrar TODOS los productos que coincidan (destacados primero)
          const params = { limit: 50 };
          
          // Solo filtrar por featured si NO hay búsqueda activa
          if (!filters.search) {
            params.featured_only = true;
          }
          
          if (filters.search) params.search = filters.search;
          if (filters.category) params.category = filters.category;
          if (filters.subcategory) params.subcategory = filters.subcategory;
          if (filters.origin) params.origin = filters.origin;
          if (filters.availability) params.availability = filters.availability;
          if (filters.sample_available) params.sample_available = filters.sample_available === 'true';
          if (filters.product_type) params.product_type = filters.product_type;
          if (regionFilter) params.region = regionFilter;
          
          const data = await getProducts(params);
          setProducts(data);
        } else if (searchType === 'tiendas') {
          // Tiendas en línea - show store cards, not products
          const params = { limit: 50 };
          if (filters.search) params.search = filters.search;
          if (regionFilter) params.region = regionFilter;
          
          const data = await searchStores(params);
          setStores(data);
        } else if (searchType === 'providers') {
          // Proveedores - companies that sell products
          const params = { limit: 200 };
          if (filters.search) params.search = filters.search;
          if (filters.state) params.state = filters.state;
          if (filters.verified) params.verified = filters.verified === 'true';
          if (regionFilter) params.region = regionFilter;
          
          const allData = await getCompanies(params);
          // Filter for providers: is_proveedor = true OR company_type includes proveedor
          let filtered = allData.filter(c => 
            c.is_proveedor === true || 
            c.company_type === 'proveedor' || 
            c.company_type === 'ambos'
          );
          
          // Sort: sponsored first, then by rating
          filtered.sort((a, b) => {
            if (a.is_sponsored && !b.is_sponsored) return -1;
            if (!a.is_sponsored && b.is_sponsored) return 1;
            return (b.rating_average || 0) - (a.rating_average || 0);
          });
          
          // Apply subcategory filter if selected
          if (filters.proveedor_subcategoria) {
            filtered = filtered.filter(c => c[`proveedor_${filters.proveedor_subcategoria}`] === true);
          }
          
          setCompanies(filtered);
        } else if (searchType === 'services') {
          // Servicios/Maquila - companies that offer services
          const params = { limit: 200 };
          if (filters.search) params.search = filters.search;
          if (filters.state) params.state = filters.state;
          if (filters.verified) params.verified = filters.verified === 'true';
          if (regionFilter) params.region = regionFilter;
          
          const allData = await getCompanies(params);
          // Filter for maquileros: is_maquilero = true OR company_type = fabricante
          let filtered = allData.filter(c => 
            c.is_maquilero === true || 
            c.company_type === 'fabricante' || 
            c.company_type === 'ambos'
          );
          
          // Sort: sponsored first, then by rating
          filtered.sort((a, b) => {
            if (a.is_sponsored && !b.is_sponsored) return -1;
            if (!a.is_sponsored && b.is_sponsored) return 1;
            return (b.rating_average || 0) - (a.rating_average || 0);
          });
          
          // Apply subcategory filter if selected
          if (filters.maquilero_subcategoria) {
            filtered = filtered.filter(c => c[`maquilero_${filters.maquilero_subcategoria}`] === true);
          }
          
          setCompanies(filtered);
        } else if (searchType === 'fabricantes') {
          // Fabricantes - companies that buy materials
          const params = { limit: 200 };
          if (filters.search) params.search = filters.search;
          if (filters.state) params.state = filters.state;
          if (filters.verified) params.verified = filters.verified === 'true';
          if (regionFilter) params.region = regionFilter;
          
          const allData = await getCompanies(params);
          // Filter for fabricantes: is_comprador = true
          let filtered = allData.filter(c => c.is_comprador === true);
          
          // Sort: sponsored first, then by rating
          filtered.sort((a, b) => {
            if (a.is_sponsored && !b.is_sponsored) return -1;
            if (!a.is_sponsored && b.is_sponsored) return 1;
            return (b.rating_average || 0) - (a.rating_average || 0);
          });
          
          setCompanies(filtered);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [searchType, filters, regionFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    // Filters already trigger search via useEffect
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      category: '',
      subcategory: '',
      origin: '',
      availability: '',
      sample_available: '',
      state: '',
      company_type: '',
      verified: '',
      proveedor_subcategoria: '',
      maquilero_subcategoria: ''
    });
  };

  const subcategories = filters.category && categories[filters.category]
    ? categories[filters.category].subcategories
    : [];

  // Get subcategory labels for display
  const getSubcategoryLabel = (subKey) => {
    if (filters.category && categories[filters.category]?.subcategory_labels) {
      return categories[filters.category].subcategory_labels[subKey] || subKey;
    }
    return subKey;
  };

  const activeFiltersCount = Object.values(filters).filter(v => v && v !== '').length;

  return (
    <div className="min-h-screen bg-slate-50 pb-20 lg:pb-0">
      {/* Unified Public Header */}
      <PublicHeader showBack={true} showSearch={false} />
      
      {/* Search Header */}
      <div className="bg-white border-b border-slate-200 sticky top-16 z-30">
        <div className="container-app py-4">
          <form onSubmit={handleSearch}>
            <div className="flex gap-4">
              <div className="relative flex-1">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder={
                    searchType === 'catalogo' 
                      ? (isUSA ? 'Search all products in catalog...' : 'Buscar en todo el catálogo...') 
                      : searchType === 'tiendas'
                        ? (isUSA ? 'Search online stores...' : 'Buscar tiendas en línea...')
                        : searchType === 'providers'
                          ? (isUSA ? 'Search material suppliers...' : 'Buscar proveedores de insumos...')
                          : searchType === 'services'
                            ? (isUSA ? 'Search manufacturing services...' : 'Buscar servicios de maquila...')
                            : (isUSA ? 'Search manufacturers...' : 'Buscar fabricantes...')
                  }
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10 h-12"
                  data-testid="search-input"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                className="h-12 px-4"
                onClick={() => setShowFilters(!showFilters)}
              >
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                {isUSA ? 'Filters' : 'Filtros'}
                {activeFiltersCount > 0 && (
                  <Badge className="ml-2 bg-emerald-600">{activeFiltersCount}</Badge>
                )}
              </Button>
            </div>
          </form>

          {/* Region Filter + Search Type Toggle */}
          <div className="flex flex-wrap items-center gap-2 mt-4">
            {/* Region Filter - First */}
            <div className="flex items-center gap-2 pr-3 border-r border-slate-300">
              <span className="text-sm font-medium text-slate-600">{isUSA ? 'Region:' : 'Región:'}</span>
              <Select value={regionFilter || 'all'} onValueChange={(v) => setRegionFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[130px] h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isUSA ? 'All' : 'Todas'}</SelectItem>
                  <SelectItem value="MX">🇲🇽 México</SelectItem>
                  <SelectItem value="US">🇺🇸 USA</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Product Type Filter (only for Catalog) */}
            {searchType === 'catalogo' && (
              <div className="flex items-center gap-2 pr-3 border-r border-slate-300">
                <span className="text-sm font-medium text-slate-600">{isUSA ? 'Type:' : 'Tipo:'}</span>
                <Select value={filters.product_type || 'all'} onValueChange={(v) => setFilters({...filters, product_type: v === 'all' ? '' : v})}>
                  <SelectTrigger className="w-[160px] h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {productTypes.map(type => (
                      <SelectItem key={type.value || 'all'} value={type.value || 'all'}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Search Type Tabs */}
            <div className="flex gap-2 overflow-x-auto pb-1">
              <Button
                variant={searchType === 'catalogo' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSearchType('catalogo')}
                className={searchType === 'catalogo' ? 'bg-amber-600 hover:bg-amber-700' : ''}
                data-testid="search-catalogo-tab"
              >
                <Star className="w-4 h-4 mr-2" />
                {isUSA ? 'Featured' : 'Catálogo'}
              </Button>
              <Button
                variant={searchType === 'tiendas' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSearchType('tiendas')}
                className={searchType === 'tiendas' ? 'bg-emerald-700' : ''}
                data-testid="search-tiendas-tab"
              >
                <Store className="w-4 h-4 mr-2" />
                {isUSA ? 'Stores' : 'Tiendas'}
              </Button>
              <Button
                variant={searchType === 'providers' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSearchType('providers')}
                className={searchType === 'providers' ? 'bg-blue-700' : ''}
                data-testid="search-providers-tab"
              >
                <Building2 className="w-4 h-4 mr-2" />
                {isUSA ? 'Suppliers' : 'Proveedores'}
              </Button>
              <Button
                variant={searchType === 'services' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSearchType('services')}
                className={searchType === 'services' ? 'bg-purple-700' : ''}
                data-testid="search-services-tab"
              >
                <Wrench className="w-4 h-4 mr-2" />
                {isUSA ? 'Manufacturing' : 'Maquileros'}
              </Button>
              <Button
                variant={searchType === 'fabricantes' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSearchType('fabricantes')}
                className={searchType === 'fabricantes' ? 'bg-slate-700' : ''}
                data-testid="search-fabricantes-tab"
              >
                <Users className="w-4 h-4 mr-2" />
                {isUSA ? 'Manufacturers' : 'Fabricantes'}
              </Button>
            </div>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="border-t border-slate-200 bg-slate-50 py-4">
            <div className="container-app">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900">{isUSA ? 'Filters' : 'Filtros'}</h3>
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="w-4 h-4 mr-1" />
                  {isUSA ? 'Clear filters' : 'Limpiar filtros'}
                </Button>
              </div>

              {searchType === 'catalogo' ? (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">{isUSA ? 'Category' : 'Categoría'}</Label>
                    <Select
                      value={filters.category || 'all'}
                      onValueChange={(value) => setFilters({ ...filters, category: value === 'all' ? '' : value, subcategory: '' })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={isUSA ? 'All' : 'Todas'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{isUSA ? 'All' : 'Todas'}</SelectItem>
                        {Object.entries(categories).map(([key, cat]) => (
                          <SelectItem key={key} value={key}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">{isUSA ? 'Subcategory' : 'Subcategoría'}</Label>
                    <Select
                      value={filters.subcategory || 'all'}
                      onValueChange={(value) => setFilters({ ...filters, subcategory: value === 'all' ? '' : value })}
                      disabled={!filters.category}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={isUSA ? 'All' : 'Todas'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{isUSA ? 'All' : 'Todas'}</SelectItem>
                        {subcategories.map((sub) => (
                          <SelectItem key={sub} value={sub}>{getSubcategoryLabel(sub)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">{isUSA ? 'Origin' : 'Origen'}</Label>
                    <Select
                      value={filters.origin || 'all'}
                      onValueChange={(value) => setFilters({ ...filters, origin: value === 'all' ? '' : value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={isUSA ? 'All' : 'Todos'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{isUSA ? 'All' : 'Todos'}</SelectItem>
                        <SelectItem value="nacional">{isUSA ? 'Domestic' : 'Nacional'}</SelectItem>
                        <SelectItem value="importado">{isUSA ? 'Imported' : 'Importado'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">{isUSA ? 'Availability' : 'Disponibilidad'}</Label>
                    <Select
                      value={filters.availability || 'all'}
                      onValueChange={(value) => setFilters({ ...filters, availability: value === 'all' ? '' : value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={isUSA ? 'All' : 'Todas'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{isUSA ? 'All' : 'Todas'}</SelectItem>
                        <SelectItem value="disponible">{isUSA ? 'Available' : 'Disponible'}</SelectItem>
                        <SelectItem value="por_pedido">{isUSA ? 'On order' : 'Por pedido'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">{isUSA ? 'Sample' : 'Muestra física'}</Label>
                    <Select
                      value={filters.sample_available || 'all'}
                      onValueChange={(value) => setFilters({ ...filters, sample_available: value === 'all' ? '' : value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={isUSA ? 'All' : 'Todas'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{isUSA ? 'All' : 'Todas'}</SelectItem>
                        <SelectItem value="true">{isUSA ? 'Available' : 'Disponible'}</SelectItem>
                        <SelectItem value="false">{isUSA ? 'Not available' : 'No disponible'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">{isUSA ? 'State' : 'Estado'}</Label>
                    <Select
                      value={filters.state || 'all'}
                      onValueChange={(value) => setFilters({ ...filters, state: value === 'all' ? '' : value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={isUSA ? 'All' : 'Todos'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{isUSA ? 'All' : 'Todos'}</SelectItem>
                        {statesList.map((state) => (
                          <SelectItem key={state} value={state}>{state}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Subcategoría de Proveedor */}
                  {searchType === 'providers' && (
                    <div className="space-y-1">
                      <Label className="text-xs">{isUSA ? 'Product type' : 'Tipo de producto'}</Label>
                      <Select
                        value={filters.proveedor_subcategoria || 'all'}
                        onValueChange={(value) => setFilters({ ...filters, proveedor_subcategoria: value === 'all' ? '' : value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={isUSA ? 'All' : 'Todos'} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{isUSA ? 'All' : 'Todos'}</SelectItem>
                          <SelectItem value="telas">{isUSA ? 'Fabrics' : 'Telas'}</SelectItem>
                          <SelectItem value="avios">{isUSA ? 'Trims' : 'Avíos'}</SelectItem>
                          <SelectItem value="etiquetas">{isUSA ? 'Labels' : 'Etiquetas'}</SelectItem>
                          <SelectItem value="hilos">{isUSA ? 'Threads & fibers' : 'Hilos y fibras'}</SelectItem>
                          <SelectItem value="empaque">{isUSA ? 'Packaging' : 'Empaque'}</SelectItem>
                          <SelectItem value="otros">{isUSA ? 'Others' : 'Otros'}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Subcategoría de Maquilero */}
                  {searchType === 'services' && (
                    <div className="space-y-1">
                      <Label className="text-xs">{isUSA ? 'Service type' : 'Tipo de servicio'}</Label>
                      <Select
                        value={filters.maquilero_subcategoria || 'all'}
                        onValueChange={(value) => setFilters({ ...filters, maquilero_subcategoria: value === 'all' ? '' : value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={isUSA ? 'All' : 'Todos'} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{isUSA ? 'All' : 'Todos'}</SelectItem>
                          <SelectItem value="corte">{isUSA ? 'Cutting' : 'Corte'}</SelectItem>
                          <SelectItem value="confeccion">{isUSA ? 'Sewing' : 'Confección'}</SelectItem>
                          <SelectItem value="acabados">{isUSA ? 'Finishing' : 'Acabados'}</SelectItem>
                          <SelectItem value="bordado">{isUSA ? 'Embroidery' : 'Bordado'}</SelectItem>
                          <SelectItem value="estampado">{isUSA ? 'Printing' : 'Estampado'}</SelectItem>
                          <SelectItem value="tenido">{isUSA ? 'Dyeing' : 'Teñido'}</SelectItem>
                          <SelectItem value="otros">{isUSA ? 'Others' : 'Otros'}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="space-y-1">
                    <Label className="text-xs">{isUSA ? 'Verified' : 'Verificado'}</Label>
                    <Select
                      value={filters.verified || 'all'}
                      onValueChange={(value) => setFilters({ ...filters, verified: value === 'all' ? '' : value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={isUSA ? 'All' : 'Todos'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{isUSA ? 'All' : 'Todos'}</SelectItem>
                        <SelectItem value="true">{isUSA ? 'Verified' : 'Verificados'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      <div className="container-app py-8">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : searchType === 'catalogo' ? (
          <>
            {/* Header changes based on search state */}
            <div className="flex items-center gap-2 mb-6">
              {filters.search ? (
                <>
                  <Package className="w-5 h-5 text-slate-500" />
                  <p className="text-sm text-slate-500">
                    {products.length} {isUSA ? 'products found for' : 'productos encontrados para'} "{filters.search}"
                  </p>
                </>
              ) : (
                <>
                  <Star className="w-5 h-5 text-amber-500" />
                  <p className="text-sm text-slate-500">{products.length} {isUSA ? 'featured products' : 'productos destacados'}</p>
                </>
              )}
            </div>
            {products.length === 0 ? (
              <div className="empty-state">
                {filters.search ? (
                  <>
                    <Package className="empty-state-icon" />
                    <h3 className="empty-state-title">{isUSA ? 'No products found' : 'No se encontraron productos'}</h3>
                    <p className="empty-state-text">{isUSA ? 'Try other search terms or filters' : 'Intenta con otros términos de búsqueda o filtros'}</p>
                  </>
                ) : (
                  <>
                    <Star className="empty-state-icon text-amber-400" />
                    <h3 className="empty-state-title">{isUSA ? 'No featured products yet' : 'Aún no hay productos destacados'}</h3>
                    <p className="empty-state-text">{isUSA ? 'Featured products from PRO suppliers will appear here. Search to explore all products.' : 'Aquí aparecerán los productos destacados de proveedores PRO. Busca para explorar todos los productos.'}</p>
                  </>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {products.map((product) => (
                  <Card 
                    key={product.product_id} 
                    className={`product-card cursor-pointer relative overflow-hidden ${product.is_featured ? 'border-amber-200 ring-1 ring-amber-200' : ''}`}
                    onClick={() => navigate(`/products/${product.product_id}`)}
                    data-testid={`search-product-${product.product_id}`}
                  >
                    {/* Featured badge - only show if product is featured */}
                    {product.is_featured && (
                      <div className="absolute top-2 right-2 z-10">
                        <Badge className="bg-amber-500 text-white">
                          <Star className="w-3 h-3 mr-1" />
                          {isUSA ? 'Featured' : 'Destacado'}
                        </Badge>
                      </div>
                    )}
                    {product.images && product.images.length > 0 ? (
                      <div 
                        className="relative cursor-pointer group"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setImageModal({
                            isOpen: true,
                            src: getFirstImage(product.images),
                            title: isUSA && product.name_en ? product.name_en : product.name
                          });
                        }}
                      >
                        <img 
                          src={getFirstImage(product.images)} 
                          alt={isUSA && product.name_en ? product.name_en : product.name}
                          className="product-card-image"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                          <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    ) : (
                      <div className="product-card-image flex flex-col items-center justify-center bg-slate-50">
                        <ImageIcon className="w-10 h-10 text-slate-300 mb-1" />
                        <span className="text-xs text-slate-400">{isUSA ? 'No image' : 'Sin imagen'}</span>
                      </div>
                    )}
                    <CardContent className="product-card-content">
                      <h3 className="font-semibold text-slate-900 line-clamp-1">{isUSA && product.name_en ? product.name_en : product.name}</h3>
                      <p className="text-sm text-slate-500 line-clamp-1">{product.company_name}</p>
                      
                      <div className="mt-2 flex flex-wrap gap-1">
                        {product.composition && (
                          <Badge variant="outline" className="text-xs">{product.composition}</Badge>
                        )}
                        {product.origin === 'importado' && (
                          <Badge variant="outline" className="text-xs text-blue-600 border-blue-200">{isUSA ? 'Imported' : 'Importado'}</Badge>
                        )}
                      </div>

                      <div className="mt-3 flex items-center justify-between">
                        {!product.hide_price && product.reference_price ? (
                          <span className="font-semibold text-slate-900">
                            ${product.reference_price.toLocaleString()} {product.currency || 'MXN'}/{product.unit || (product.category === 'telas' ? 'Mt' : (isUSA ? 'Unit' : 'pza'))}
                          </span>
                        ) : (
                          <span className="text-sm text-slate-500">{isUSA ? 'Price on request' : 'Precio bajo cotización'}</span>
                        )}
                        {product.sample_available && (
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs">{isUSA ? 'Sample' : 'Muestra'}</Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        ) : searchType === 'tiendas' ? (
          <>
            <p className="text-sm text-slate-500 mb-6">{stores.length} {isUSA ? 'online stores found' : 'tiendas en línea encontradas'}</p>
            {stores.length === 0 ? (
              <div className="empty-state">
                <Store className="empty-state-icon" />
                <h3 className="empty-state-title">{isUSA ? 'No online stores found' : 'No se encontraron tiendas en línea'}</h3>
                <p className="empty-state-text">{isUSA ? 'Try other search terms' : 'Intenta con otros términos de búsqueda'}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {stores.map((store) => (
                  <Card 
                    key={store.store_id || store.company_id} 
                    className="card-industrial hover-lift cursor-pointer"
                    onClick={() => navigate(store.store_slug ? `/tienda/${store.store_slug}` : `/company/${store.company_id}`)}
                    data-testid={`search-store-${store.store_id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        {store.logo_url || store.store_logo ? (
                          <img 
                            src={store.logo_url || store.store_logo} 
                            alt={store.company_name || store.store_name}
                            className="w-16 h-16 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-emerald-100 rounded-lg flex items-center justify-center">
                            <Store className="w-8 h-8 text-emerald-600" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-slate-900 truncate">{store.store_name || store.company_name}</h3>
                            {store.verified && (
                              <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                            )}
                            {store.is_featured && (
                              <Star className="w-4 h-4 text-amber-500 flex-shrink-0" />
                            )}
                          </div>
                          {store.company_name && store.store_name && store.company_name !== store.store_name && (
                            <p className="text-sm text-slate-500">{store.company_name}</p>
                          )}
                          <div className="flex items-center gap-1 text-sm text-slate-500 mt-1">
                            <MapPin className="w-3 h-3" />
                            {store.city}, {store.state}
                          </div>
                        </div>
                      </div>

                      {store.description && (
                        <p className="text-sm text-slate-600 mt-4 line-clamp-2">
                          {store.description}
                        </p>
                      )}

                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                        <div className="flex items-center gap-2">
                          <ShoppingBag className="w-4 h-4 text-slate-400" />
                          <span className="text-sm text-slate-600">
                            {store.product_count || 0} {isUSA ? 'products' : 'productos'}
                          </span>
                        </div>
                        {store.subscription_plan === 'pro' && (
                          <Badge className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white border-0 text-xs">
                            PRO
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <p className="text-sm text-slate-500 mb-6">
              {companies.length} {searchType === 'services' 
                ? (isUSA ? 'manufacturing services found' : 'maquileros/servicios encontrados') 
                : searchType === 'fabricantes' 
                  ? (isUSA ? 'manufacturers found' : 'fabricantes encontrados') 
                  : (isUSA ? 'suppliers found' : 'proveedores encontrados')}
            </p>
            {companies.length === 0 ? (
              <div className="empty-state">
                {searchType === 'services' ? (
                  <Wrench className="empty-state-icon" />
                ) : searchType === 'fabricantes' ? (
                  <Users className="empty-state-icon" />
                ) : (
                  <Building2 className="empty-state-icon" />
                )}
                <h3 className="empty-state-title">
                  {searchType === 'services' 
                    ? (isUSA ? 'No manufacturing services found' : 'No se encontraron maquileros/servicios') 
                    : searchType === 'fabricantes' 
                      ? (isUSA ? 'No manufacturers found' : 'No se encontraron fabricantes') 
                      : (isUSA ? 'No suppliers found' : 'No se encontraron proveedores')}
                </h3>
                <p className="empty-state-text">{isUSA ? 'Try other filters or search terms' : 'Intenta con otros filtros o términos de búsqueda'}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {companies.map((company) => (
                  <Card 
                    key={company.company_id} 
                    className="card-industrial hover-lift cursor-pointer"
                    onClick={() => navigate(`/company/${company.company_id}`)}
                    data-testid={`search-company-${company.company_id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        {company.logo_url ? (
                          <img 
                            src={company.logo_url} 
                            alt={company.company_name}
                            className="w-14 h-14 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-14 h-14 bg-slate-100 rounded-lg flex items-center justify-center">
                            <Building2 className="w-7 h-7 text-slate-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-slate-900 truncate">{company.company_name || company.name}</h3>
                            {(company.verified || company.is_verified) && (
                              <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-sm text-slate-500">
                            {company.company_type === 'proveedor' 
                              ? (isUSA ? 'Supplier' : 'Proveedor') 
                              : company.company_type === 'fabricante' 
                                ? (isUSA ? 'Manufacturer' : 'Fabricante') 
                                : (isUSA ? 'Supplier and Manufacturer' : 'Proveedor y Fabricante')}
                          </p>
                          <div className="flex items-center gap-1 text-sm text-slate-500 mt-1">
                            <MapPin className="w-3 h-3" />
                            {company.location_city || company.city}, {company.location_state || company.state}
                          </div>
                        </div>
                      </div>

                      {(company.description || company.description_en) && (
                        <p className="text-sm text-slate-600 mt-4 line-clamp-2">
                          {isUSA && company.description_en ? company.description_en : company.description}
                        </p>
                      )}

                      {company.certifications && company.certifications.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-3">
                          {company.certifications.slice(0, 3).map((cert, index) => (
                            <Badge key={index} variant="outline" className="text-xs">{cert}</Badge>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
      
      {/* Image Modal for enlarging product images */}
      <ImageModal 
        isOpen={imageModal.isOpen}
        onClose={() => setImageModal({ isOpen: false, src: '', title: '' })}
        imageSrc={imageModal.src}
        title={imageModal.title}
      />
    </div>
  );
};

export default Search;
